import React from 'react';
import { Text, Flex } from '@podiumhq/design-system';
import { useTracking } from '@podiumhq/tracking';

import { useTrialsContext } from 'contexts/TrialsContextProvider';
import useChangePage from 'hooks/useChangePage';
import { TRIAL_URL } from 'routes/constants';

import useSubmitQuestionnaireAnswers from '../../hooks/useSubmitQuestionnaireAnswer';
import { FakeWrapper } from '../../layout';
import { Subheading } from '../../styles';

import campaignsIcon from './assets/campaigns.svg';
import paymentsIcon from './assets/payments.svg';
import reviewsIcon from './assets/reviews.svg';
import webchatIcon from './assets/webchat.svg';
import inboxIcon from './assets/inbox.svg';
import Card from './Card';

const QUESTION_SLUG = 'most-useful-features';
const PROFILE_PATH = `${TRIAL_URL}/profile`;

const Intent = ({ trackingEventPath = 'trials.intentPage' }) => {
  const { submit: submitAnswers } = useSubmitQuestionnaireAnswers();
  const { setIntent } = useTrialsContext() || {};
  const changePage = useChangePage();
  const { trackEvent } = useTracking();

  const handleIntentClick = (card) => () => {
    trackEvent(`${trackingEventPath}.intentClicked`, { value: card.value });
    setIntent(card.value);
    submitAnswers({
      answers: [{ questionSlug: QUESTION_SLUG, answer: card.value }],
    })
      .then(() => changePage(PROFILE_PATH))
      .catch((error) =>
        trackEvent(`${trackingEventPath}.error`, {
          error: JSON.stringify(error),
        })
      );
  };

  const handleSkipClick = () => {
    trackEvent(`${trackingEventPath}.skip`);
    changePage(PROFILE_PATH);
  };

  return (
    <FakeWrapper title="What do you want to try first?">
      <Subheading>
        Tell us what you’re hoping to achieve, and we’ll give you personalized
        suggestions throughout the app.{' '}
      </Subheading>

      {CARDS.map((card) => (
        <Card
          showArrow
          icon={card.icon}
          header={card.header}
          content={card.content}
          onCardClick={handleIntentClick(card)}
        />
      ))}

      <Flex justifyContent="center" marginTop="24px">
        <Text
          color="blue.base"
          cursor="pointer"
          fontSize="md"
          fontWeight="semibold"
          onClick={handleSkipClick}
          textDecoration="none"
        >
          Skip
        </Text>
      </Flex>
    </FakeWrapper>
  );
};

const CARDS = [
  {
    header: 'Reviews',
    content: 'Get more reviews and improve your online presence',
    linkTo: '/walkthrough?flow=inbox-send-review',
    excludeUrlParam: true,
    icon: reviewsIcon,
    value: 'reviews',
  },
  {
    header: 'Webchat',
    content: 'Add a chat-to-text tool to your website',
    linkTo: '/account/settings/webchat-onboarding/verify-website',
    icon: webchatIcon,
    value: 'webchat',
  },
  {
    header: 'Payments',
    content: 'Request money via text and charge cards',
    linkTo: '/payments/welcome',
    icon: paymentsIcon,
    value: 'payments',
  },
  {
    header: 'Campaigns',
    content: 'Get marketing opt-ins and send offers',
    linkTo: '/campaigns',
    icon: campaignsIcon,
    value: 'campaigns',
  },
  {
    header: 'Texting Customers',
    content: 'Respond in 5 mins or less, everytime',
    linkTo: '/walkthrough?flow=inbox-send-message',
    excludeUrlParam: true,
    icon: inboxIcon,
    value: 'inbox',
  },
];

export default Intent;
